import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { UUID_REGEX } from '../utils/slug';

type Club = {
  id: string;
  slug: string;
  name: string;
  leader_id: string | null;
};

type RouteRecord = {
  id: string;
  club_id: string;
  name: string;
  description: string | null;
  route_type: 'road' | 'trail' | 'mixed' | null;
  start_location: string | null;
  distance_miles: number | null;
  elevation_gain_ft: number | null;
  surface: 'road' | 'trail' | 'track' | 'mixed' | 'gravel' | null;
  external_url: string | null;
  gpx_file_url: string | null;
  is_public: boolean;
  created_at: string;
};

type RouteForm = {
  name: string;
  description: string;
  route_type: '' | 'road' | 'trail' | 'mixed';
  start_location: string;
  distance_miles: string;
  elevation_gain_ft: string;
  surface: '' | 'road' | 'trail' | 'track' | 'mixed' | 'gravel';
  external_url: string;
  is_public: boolean;
};

const emptyForm: RouteForm = {
  name: '',
  description: '',
  route_type: '',
  start_location: '',
  distance_miles: '',
  elevation_gain_ft: '',
  surface: '',
  external_url: '',
  is_public: true,
};

function formatDistance(distance?: number | null) {
  if (distance == null) return null;
  return `${distance} mi`;
}

function formatElevation(elevation?: number | null) {
  if (elevation == null) return null;
  return `${elevation.toLocaleString()} ft gain`;
}

export default function ManageRoutes() {
  const { id } = useParams();
  const { user, leader, canManageClub, loading: authLoading } = useAuth();

  const [club, setClub] = useState<Club | null>(null);
  const [routes, setRoutes] = useState<RouteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  const [form, setForm] = useState<RouteForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [deletingRouteId, setDeletingRouteId] = useState<string | null>(null);

  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    async function loadPage() {
      if (!id || !leader) return;

      setLoading(true);
      setErrorMessage(null);

      const isUuid = UUID_REGEX.test(id);
      const { data: clubData, error: clubError } = await (
        isUuid
          ? supabase.from('clubs').select('id, slug, name, leader_id').eq('id', id).single()
          : supabase.from('clubs').select('id, slug, name, leader_id').eq('slug', id).single()
      );

      const clubUuid = (clubData as Club | null)?.id ?? id;

      const [{ data: routeData, error: routeError }] =
        await Promise.all([
          supabase.from('routes').select('*').eq('club_id', clubUuid).order('created_at', {
            ascending: false,
          }),
        ]);

      if (clubError || !clubData) {
        setErrorMessage(clubError?.message || 'Unable to load club.');
        setAllowed(false);
        setLoading(false);
        return;
      }

      const typedClub = clubData as Club;
      // club_leaders membership is the authority (admins manage everything)
      const canManage = canManageClub(typedClub.id);

      setClub(typedClub);
      setAllowed(canManage);

      if (!canManage) {
        setLoading(false);
        return;
      }

      if (routeError) {
        setErrorMessage(routeError.message);
      } else {
        setRoutes((routeData || []) as RouteRecord[]);
      }

      setLoading(false);
    }

    if (!authLoading) {
      loadPage();
    }
  }, [authLoading, id, leader, canManageClub]);

  const pageTitle = useMemo(() => {
    if (!club) return 'Manage Routes';
    return `Manage Routes · ${club.name}`;
  }, [club]);

  function updateField<K extends keyof RouteForm>(key: K, value: RouteForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function resetForm() {
    setForm(emptyForm);
    setEditingRouteId(null);
  }

  function startEdit(route: RouteRecord) {
    setEditingRouteId(route.id);
    setMessage(null);
    setErrorMessage(null);

    setForm({
      name: route.name || '',
      description: route.description || '',
      route_type: route.route_type || '',
      start_location: route.start_location || '',
      distance_miles:
        route.distance_miles === null || route.distance_miles === undefined
          ? ''
          : String(route.distance_miles),
      elevation_gain_ft:
        route.elevation_gain_ft === null || route.elevation_gain_ft === undefined
          ? ''
          : String(route.elevation_gain_ft),
      surface: route.surface || '',
      external_url: route.external_url || '',
      is_public: route.is_public,
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function refreshRoutes() {
    if (!club?.id) return;

    const { data, error } = await supabase
      .from('routes')
      .select('*')
      .eq('club_id', club.id)
      .order('created_at', { ascending: false });

    if (error) {
      setErrorMessage(error.message);
      return;
    }

    setRoutes((data || []) as RouteRecord[]);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!id || !leader) return;

    setSaving(true);
    setMessage(null);
    setErrorMessage(null);

    try {
      if (!form.name.trim()) {
        throw new Error('Route name is required.');
      }

      const payload = {
        club_id: club!.id,
        name: form.name.trim(),
        description: form.description.trim() || null,
        route_type: form.route_type || null,
        start_location: form.start_location.trim() || null,
        distance_miles: form.distance_miles.trim() ? Number(form.distance_miles) : null,
        elevation_gain_ft: form.elevation_gain_ft.trim() ? Number(form.elevation_gain_ft) : null,
        surface: form.surface || null,
        external_url: form.external_url.trim() || null,
        is_public: form.is_public,
        created_by: leader.id,
      };

      if (payload.distance_miles !== null && Number.isNaN(payload.distance_miles)) {
        throw new Error('Distance must be a valid number.');
      }

      if (payload.elevation_gain_ft !== null && Number.isNaN(payload.elevation_gain_ft)) {
        throw new Error('Elevation gain must be a valid number.');
      }

      if (editingRouteId) {
        const { error } = await supabase
          .from('routes')
          .update({
            name: payload.name,
            description: payload.description,
            route_type: payload.route_type,
            start_location: payload.start_location,
            distance_miles: payload.distance_miles,
            elevation_gain_ft: payload.elevation_gain_ft,
            surface: payload.surface,
            external_url: payload.external_url,
            is_public: payload.is_public,
          })
          .eq('id', editingRouteId);

        if (error) throw new Error(error.message);

        setMessage('Route updated.');
      } else {
        const { error } = await supabase.from('routes').insert(payload);

        if (error) throw new Error(error.message);

        setMessage('Route created.');
      }

      await refreshRoutes();
      resetForm();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to save route.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(routeId: string) {
    setDeletingRouteId(routeId);
    setMessage(null);
    setErrorMessage(null);

    const { error } = await supabase.from('routes').delete().eq('id', routeId);

    if (error) {
      setErrorMessage(error.message);
      setDeletingRouteId(null);
      return;
    }

    setRoutes((prev) => prev.filter((route) => route.id !== routeId));
    setMessage('Route deleted.');

    if (editingRouteId === routeId) {
      resetForm();
    }

    setDeletingRouteId(null);
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-5xl rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <div className="h-8 w-56 animate-pulse rounded bg-black/10" />
          <div className="mt-4 h-4 w-72 animate-pulse rounded bg-black/10" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!leader) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-black">Leader profile required</h1>
          <p className="mt-3 text-black/65">
            Your account is signed in, but it is not linked to a run leader record yet.
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="rounded-2xl border border-black px-5 py-3 text-sm font-medium text-black transition hover:bg-black hover:text-white"
            >
              Back Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-5xl rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <div className="h-8 w-56 animate-pulse rounded bg-black/10" />
          <div className="mt-4 h-4 w-72 animate-pulse rounded bg-black/10" />
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-black">Access denied</h1>
          <p className="mt-3 text-black/65">
            This club is not linked to your leader account, and your account does not have admin
            access.
          </p>
          <div className="mt-6 flex gap-3">
            <Link
              to="/explore"
              className="rounded-2xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:opacity-90"
            >
              Back to Explore
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-16">
      <div className="mx-auto max-w-6xl px-6 pt-8">
        <div className="overflow-hidden rounded-[2rem] border border-black/8 bg-white shadow-sm">
          <div className="bg-gradient-to-br from-black to-neutral-700 px-8 py-10 text-white">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-white/70">
              Club Route Library
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl">
              {pageTitle}
            </h1>
            <p className="mt-3 max-w-2xl text-base text-white/80">
              Build a reusable library of routes for this club, then attach them to recurring runs
              in the next step.
            </p>

            {club && (
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/clubs/${club.slug}`}
                  className="rounded-2xl bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:opacity-90"
                >
                  View Club
                </Link>
                <Link
                  to={`/clubs/${club.slug}/edit`}
                  className="rounded-2xl border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white hover:text-black"
                >
                  Edit Club
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="space-y-6">
            <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-sm md:p-8">
              <h2 className="text-xl font-semibold text-black">
                {editingRouteId ? 'Edit route' : 'Create route'}
              </h2>
              <p className="mt-2 text-sm text-black/60">
                Add route details now. GPX upload comes in a later phase.
              </p>

              {message && (
                <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {message}
                </div>
              )}

              {errorMessage && (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-black">Route name</label>
                  <input
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    placeholder="Thursday Night Loop"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    rows={4}
                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    placeholder="Great route for social miles with one steady climb."
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">Route type</label>
                    <select
                      value={form.route_type}
                      onChange={(e) =>
                        updateField('route_type', e.target.value as RouteForm['route_type'])
                      }
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    >
                      <option value="">Select</option>
                      <option value="road">Road</option>
                      <option value="trail">Trail</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">Surface</label>
                    <select
                      value={form.surface}
                      onChange={(e) =>
                        updateField('surface', e.target.value as RouteForm['surface'])
                      }
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    >
                      <option value="">Select</option>
                      <option value="road">Road</option>
                      <option value="trail">Trail</option>
                      <option value="track">Track</option>
                      <option value="mixed">Mixed</option>
                      <option value="gravel">Gravel</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">
                      Start location
                    </label>
                    <input
                      value={form.start_location}
                      onChange={(e) => updateField('start_location', e.target.value)}
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                      placeholder="Double Peak Park"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">
                      External route link
                    </label>
                    <input
                      type="url"
                      value={form.external_url}
                      onChange={(e) => updateField('external_url', e.target.value)}
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                      placeholder="https://www.strava.com/routes/..."
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">
                      Distance (miles)
                    </label>
                    <input
                      value={form.distance_miles}
                      onChange={(e) => updateField('distance_miles', e.target.value)}
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                      placeholder="5.2"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">
                      Elevation gain (ft)
                    </label>
                    <input
                      value={form.elevation_gain_ft}
                      onChange={(e) => updateField('elevation_gain_ft', e.target.value)}
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                      placeholder="620"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-3 text-sm text-black">
                  <input
                    type="checkbox"
                    checked={form.is_public}
                    onChange={(e) => updateField('is_public', e.target.checked)}
                    className="h-4 w-4 rounded border-black/20"
                  />
                  Show this route publicly on the club profile
                </label>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-2xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving
                      ? editingRouteId
                        ? 'Saving...'
                        : 'Creating...'
                      : editingRouteId
                      ? 'Save route'
                      : 'Create route'}
                  </button>

                  {editingRouteId && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="rounded-2xl border border-black px-5 py-3 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-sm md:p-8">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-black">Saved routes</h2>
                  <p className="mt-2 text-sm text-black/60">
                    Reusable route cards for this club.
                  </p>
                </div>

                <div className="rounded-full border border-black/10 bg-neutral-50 px-3 py-1.5 text-xs font-medium text-black/60">
                  {routes.length} routes
                </div>
              </div>

              {routes.length === 0 ? (
                <div className="mt-6 rounded-2xl border border-dashed border-black/12 bg-neutral-50 px-5 py-8 text-sm text-black/55">
                  No routes created yet.
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {routes.map((route) => (
                    <div
                      key={route.id}
                      className="rounded-3xl border border-black/8 bg-neutral-50 p-5"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-black">{route.name}</h3>
                          <p className="mt-1 text-xs uppercase tracking-[0.12em] text-black/40">
                            {route.is_public ? 'Public' : 'Private'}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(route)}
                            className="rounded-xl border border-black px-3 py-2 text-xs font-medium text-black transition hover:bg-black hover:text-white"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(route.id)}
                            disabled={deletingRouteId === route.id}
                            className="rounded-xl border border-red-300 px-3 py-2 text-xs font-medium text-red-600 transition hover:bg-red-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {deletingRouteId === route.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {route.route_type && (
                          <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium capitalize text-black/70">
                            {route.route_type}
                          </span>
                        )}
                        {route.surface && (
                          <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium capitalize text-black/70">
                            {route.surface}
                          </span>
                        )}
                        {formatDistance(route.distance_miles) && (
                          <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-black/70">
                            {formatDistance(route.distance_miles)}
                          </span>
                        )}
                        {formatElevation(route.elevation_gain_ft) && (
                          <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-black/70">
                            {formatElevation(route.elevation_gain_ft)}
                          </span>
                        )}
                      </div>

                      {route.start_location && (
                        <p className="mt-3 text-sm text-black/65">
                          <span className="font-medium text-black">Start:</span>{' '}
                          {route.start_location}
                        </p>
                      )}

                      {route.description && (
                        <p className="mt-3 text-sm leading-7 text-black/65">
                          {route.description}
                        </p>
                      )}

                      {route.external_url && (
                        <div className="mt-4">
                          <a
                            href={route.external_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex rounded-2xl border border-black px-4 py-2 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                          >
                            Open Route
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {club && (
              <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-black">Next milestone</h2>
                <p className="mt-2 text-sm leading-6 text-black/60">
                  Once this route library is working, the next step is attaching these saved routes
                  directly to recurring runs in the club edit flow.
                </p>

                <div className="mt-4">
                  <Link
                    to={`/clubs/${club.slug}/edit`}
                    className="inline-flex rounded-2xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
                  >
                    Go to Club Editor
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}